import React from 'react';

import { Button } from '@/lib/base/buttons/button';
import { type UploadedFile } from '@/lib/files/service';

import { getFileIcon } from './utils/getFileIcon';

interface FileAttachmentProps {
  file: UploadedFile;
  onDownload?: () => void;
  onDelete?: () => void;
  showActions?: boolean;
}

export const FileAttachment: React.FC<FileAttachmentProps> = ({
  file,
  onDownload,
  onDelete,
  showActions = true,
}) => {
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) {
      return '0 Bytes';
    }
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className='flex items-center gap-3 p-3 bg-neutral-50 rounded-lg border border-neutral-200 hover:border-neutral-300 transition-colors'>
      <div className='flex-shrink-0'>
        {getFileIcon(file.original_filename, file.mime_type, { size: 40 })}
      </div>

      <div className='flex-1 min-w-0'>
        <div className='text-sm font-medium text-neutral-900 truncate'>
          {file.original_filename}
        </div>
        <div className='text-xs text-neutral-500'>
          {formatFileSize(file.file_size)}
        </div>
      </div>

      {showActions && (
        <div className='flex items-center gap-1'>
          {onDownload && (
            <Button
              type='button'
              size='sm'
              color='tertiary'
              onClick={onDownload}
              title='Download'
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='h-4 w-4'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4'
                />
              </svg>
            </Button>
          )}

          {onDelete && (
            <Button
              type='button'
              size='sm'
              color='tertiary'
              onClick={onDelete}
              title='Delete'
              className='text-error hover:text-error'
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='h-4 w-4'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
                />
              </svg>
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
